import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, setUnauthorizedHandler } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = guest, obj = user
  const [ready, setReady] = useState(false);

  useEffect(() => {
    localStorage.removeItem("llmready_token");
    setUnauthorizedHandler(() =>
      setUser((u) => {
        if (u) toast.error("Session expirée, reconnectez-vous", { id: "session-expired" });
        return u ? false : u;
      })
    );
    api
      .get("/auth/me")
      .then((r) => setUser(r.data.user))
      .catch(() => setUser(false))
      .finally(() => setReady(true));
  }, []);

  const login = useCallback(async (email, password, totpCode) => {
    const { data } = await api.post("/auth/login", { email, password, totp_code: totpCode || undefined });
    if (data.mfa_required) return { mfaRequired: true };
    setUser(data.user);
    return { user: data.user };
  }, []);

  const register = useCallback(async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    setUser(data.user);
    return data.user;
  }, []);

  const refresh = useCallback(async () => {
    const r = await api.get("/auth/me");
    setUser(r.data.user);
    return r.data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.error("Déconnexion serveur impossible", e);
    }
    setUser(false);
  }, []);

  const value = useMemo(() => ({ user, ready, login, register, logout, refresh }), [user, ready, login, register, logout, refresh]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
export { formatApiErrorDetail };
